import { Button, Card, CardContent, Grid, Tab, Typography } from "@mui/material";
import {
    a11yProps,
    gridSizeMdLg3,
    hasPermission,
    useTabsFormOnly,
} from "@/shared";
import {
    CustomAutocompleteNoForm,
    CustomTabPanel,
    CustomTextFieldNoForm,
    FormTabsOnly,
    GeneralTable,
    SampleDatePickerNoForm,
    SingleBoxTableTabsOnly,
    SingleTableBoxScene,
} from "@/shared/components/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useObtenerReporteAgendamiento } from "../../actions/reports-module.actions";
import { useTableColumnsReporteAgendamiento } from "../../shared/hooks/useTableColumnsReporteAgendamiento";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { useFetchCanalesVenta, useFetchCitiesEnabled, useFetchZones } from "@/shared/actions/erp";
import { exportExcel, mapAgendamientosToExcel } from "./function/exportarExcel";
import { PermissionsEnum } from "@/auth/interfaces";

export type ReporteAgendamientosPagePyLProps = {};

const ReporteAgendamientosPylPage: React.FC<ReporteAgendamientosPagePyLProps> = () => {

    const fechaIncial = new Date();
    fechaIncial.setDate(fechaIncial.getDate());

    const fechaMinima = new Date();
    fechaMinima.setDate(fechaMinima.getDate() - 30);

    // Estados de datos y carga
    const [allData, setAllData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {

    }, []);

    const { handleMethodChange, valueTab } = useTabsFormOnly({
        initialTabValue: 1,
    });

    const { columnsReporteAgendamiento } = useTableColumnsReporteAgendamiento();

    const [filtrosPersonalizados, setFiltrosPersonalizados] = useState({
        fecha_desde: format(fechaMinima, "yyyy-MM-dd"),
        fecha_hasta: format(fechaIncial, "yyyy-MM-dd"),
        razon_social: '',
        ciudad_id: '',
        zona_id: '',
        canal_venta_create_id: ''
    });

    const handleChange = (field: string, value: any) => {
        setFiltrosPersonalizados(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Paginación para Material React Table (manejada internamente por la tabla)
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const { data: citiesPaging, isLoading: isLoadingCities } = useFetchCitiesEnabled({
        params: {
            page_size: 600,
            estado: true,
            cobertura: true
        }
    });
    const { data: zonesPaging, isLoading: isLoadingZones } = useFetchZones(
        {
            page_size: 600,
            ciudad: filtrosPersonalizados.ciudad_id,
            estado: true
        },
        !!filtrosPersonalizados.ciudad_id
    );

    const { data: canalesVentaRes, isLoading: isLoadingCanalesVenta } = useFetchCanalesVenta({
        page_size: 1000,
        estado: true
    });

    const handleSearch = async () => {
        loadData()
    };

    const limpiarFiltros = async () => {
        handleChange('razon_social', '')
        handleChange('ciudad_id', '')
        handleChange('zona_id', '')
        handleChange('canal_venta_create_id', '')
        handleChange('fecha_desde', format(fechaMinima, "yyyy-MM-dd"))
        handleChange('fecha_hasta', format(fechaIncial, "yyyy-MM-dd"))
    }

    // AbortController para cancelar requests anteriores
    const abortControllerRef = useRef<AbortController | null>(null);
    // Hook para obtener datos
    const useObtenerReporteAgendamientoMutation = useObtenerReporteAgendamiento();
    // Cargar datos con filtros
    const loadData = useCallback(async () => {
        // Cancelar request anterior si existe
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        setIsLoading(true);

        try {
            const filters = Object.fromEntries(
                Object.entries(filtrosPersonalizados).filter(([_, value]) => {
                    return value !== '' && value !== null && value !== undefined;
                })
            );

            const response = await useObtenerReporteAgendamientoMutation.mutateAsync(filters);

            // Verificar si fue abortado
            if (abortControllerRef.current?.signal.aborted) {
                return;
            }

            if (response.data && Array.isArray(response.data)) {
                setAllData(response.data);
                // Resetear a la primera página cuando se cargan nuevos datos
                setPagination(prev => ({ ...prev, pageIndex: 0 }));
            } else {
                setAllData([]);
                toast.error("Estructura de respuesta inesperada");
            }
        } catch (err: any) {
            if (err?.name === 'AbortError' || abortControllerRef.current?.signal.aborted) {
                return; // Request cancelado, no mostrar error
            }

            // Manejar error de rango de fechas inválido
            if (err?.response?.data?.code === 'INVALID_DATE_RANGE') {
                toast.error(err.response.data.message || "Rango de fechas inválido");
            } else {
                toast.error(err?.message || "Error al cargar datos");
            }
        } finally {
            setIsLoading(false);
        }
    }, [fechaIncial, fechaMinima]);

    const filteredData = useMemo(() => {
        let data = allData;
        return data;
    }, [allData]);

    // Paginación en memoria optimizada - solo pasamos las filas visibles a la tabla
    // Esto mejora drásticamente el rendimiento porque GeneralTable solo renderiza 10-50 filas en vez de 4812
    const paginatedData = useMemo(() => {
        const startIdx = pagination.pageIndex * pagination.pageSize;
        const endIdx = startIdx + pagination.pageSize;
        return filteredData.slice(startIdx, endIdx);
    }, [filteredData, pagination.pageIndex, pagination.pageSize]);


    const totalCreaciones = allData
        .flatMap(item => item.api_trazo_venta)
        .filter(trazo => trazo.accion === "CREACION")
        .length;

    const totalRecordinados = allData.filter(item =>
        item.api_trazo_venta?.some((trazo: any) => trazo.accion === "RECORDINADO")
    ).length;

    const porcentajeRecordinaciones =
        totalCreaciones > 0
            ? ((totalRecordinados * 100) / totalCreaciones).toFixed(2)
            : "0.00";

    const promedioAgendamientos =
        allData.length > 0
            ? (totalRecordinados / allData.length).toFixed(2)
            : "0.00";

    return (
        <SingleTableBoxScene title={"Histórico de Agendamiento y Reagendamiento"}
            enableBackBtn={true}
            backPageUrl={"/operaciones/planeacion/agendamientos"}
        >
            <SingleBoxTableTabsOnly>
                <FormTabsOnly onChange={handleMethodChange} value={valueTab}>
                    <Tab label="Reporte" value={1} {...a11yProps(1)} />
                </FormTabsOnly>
            </SingleBoxTableTabsOnly>
            <Grid container spacing={3}>
                <CustomTextFieldNoForm
                    label="Nombre del Cliente"
                    defaultValue={filtrosPersonalizados?.razon_social || ''}
                    size={gridSizeMdLg3}
                    onChangeCustom={(e) => handleChange('razon_social', e.target.value)}
                    required={false}
                />
                <CustomAutocompleteNoForm
                    label="Ciudad"
                    options={citiesPaging?.data.data.filter((item) => item.estado) || []}
                    valueKey={'nombre'}
                    actualValueKey={'codigo'}
                    value={filtrosPersonalizados.ciudad_id || ''}
                    isLoadingData={isLoadingCities}
                    onChangeValue={(value) => {
                        handleChange('ciudad_id', value)
                        handleChange('zona_id', '')
                    }}
                    size={gridSizeMdLg3}
                />
                <CustomAutocompleteNoForm
                    label="Zona"
                    //name="label"
                    options={zonesPaging?.data.data.filter((item) => item.estado) || []}
                    valueKey={'nombre'}
                    actualValueKey={'codigo'}
                    value={filtrosPersonalizados.zona_id}
                    isLoadingData={isLoadingZones}
                    onChangeValue={(value) => handleChange('zona_id', value)}
                    size={gridSizeMdLg3}
                />
                <CustomAutocompleteNoForm
                    label="Canal"
                    //name="label"
                    options={canalesVentaRes?.data.data.filter((item) => item.estado) || []}
                    valueKey={'nombre'}
                    actualValueKey={'codigo'}
                    value={filtrosPersonalizados.canal_venta_create_id}
                    isLoadingData={isLoadingCanalesVenta}
                    onChangeValue={(value) => handleChange('canal_venta_create_id', value)}
                    size={gridSizeMdLg3}
                />
                <SampleDatePickerNoForm
                    label="Desde"
                    value={filtrosPersonalizados.fecha_desde || ''}
                    defaultValue={filtrosPersonalizados.fecha_desde || ''}
                    size={gridSizeMdLg3}
                    // minDate={fechaMinima}
                    // maxDate={fechaIncial}
                    onChange={(value) => handleChange('fecha_desde', value)}
                />
                <SampleDatePickerNoForm
                    label="Hasta"
                    value={filtrosPersonalizados.fecha_hasta || ''}
                    defaultValue={filtrosPersonalizados.fecha_hasta || ''}
                    size={gridSizeMdLg3}
                    // minDate={fechaIncial}
                    // maxDate={fechaMaxima}
                    onChange={(value) => handleChange('fecha_hasta', value)}
                />
            </Grid>
            <br></br>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Button variant="outlined" color="info" onClick={() => { handleSearch() }} size="small" sx={gridSizeMdLg3} disabled={isLoading}>
                        {'Generar'}
                    </Button>
                    <Button variant="outlined" color="primary" onClick={() => { limpiarFiltros() }} size="small" sx={gridSizeMdLg3} disabled={isLoading}>
                        {'Limpiar'}
                    </Button>
                    {hasPermission(PermissionsEnum.descargar_excel_agendamientos) ?
                        <Button variant="outlined" color="success" onClick={() => { exportExcel(mapAgendamientosToExcel(filteredData), 'agendamientos') }} size="small" sx={gridSizeMdLg3} disabled={isLoading}>
                            {'Exportar a Excel'}
                        </Button>
                        : ''}
                </Grid>
                <Grid item xs={6} sm={4} md={1.7}>
                    <Card
                        sx={{
                            bgcolor: '#1976d2',
                            color: '#fff',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            border: '2px solid transparent',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                        }}
                    >
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h4" fontWeight="bold" textAlign="center">
                                {allData.length}
                            </Typography>
                            <Typography variant="caption" textAlign="center" display="block" fontWeight={500}>
                                Total
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={4} md={1.7} key={"res-agendadas"}>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            border: '2px solid transparent',
                            bgcolor: 'background.paper',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 3, borderColor: 'green' },
                        }}
                    >
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h4" fontWeight="bold" textAlign="center" color={'green'}>
                                {totalCreaciones}
                            </Typography>
                            <Typography variant="caption" textAlign="center" display="block" noWrap fontWeight={500}>
                                Agendadas
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={4} md={1.7} key={"res-reagendadas"}>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            border: '2px solid transparent',
                            bgcolor: 'background.paper',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 3, borderColor: 'red' },
                        }}
                    >
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h4" fontWeight="bold" textAlign="center" color={'red'}>
                                {totalRecordinados}
                            </Typography>
                            <Typography variant="caption" textAlign="center" display="block" noWrap fontWeight={500}>
                                Reagendadas
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={4} md={1.7} key={"res-porce-reagendadas"}>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            border: '2px solid transparent',
                            bgcolor: 'background.paper',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 3, borderColor: '#000' },
                        }}
                    >
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h4" fontWeight="bold" textAlign="center" color={'#000'}>
                                {porcentajeRecordinaciones}
                            </Typography>
                            <Typography variant="caption" textAlign="center" display="block" noWrap fontWeight={500}>
                                % Reagendadas
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={1.7} key={"res-promedio"}>
                    <Card
                        sx={{
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            border: '2px solid transparent',
                            bgcolor: 'background.paper',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 3, borderColor: '#349bc4' },
                        }}
                    >
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h4" fontWeight="bold" textAlign="center" color={'#349bc4'}>
                                {promedioAgendamientos}
                            </Typography>
                            <Typography variant="caption" textAlign="center" display="block" noWrap fontWeight={500}>
                                Promedio Reagendadas
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <CustomTabPanel value={valueTab} index={1}>
                        <>
                            <GeneralTable
                                columns={columnsReporteAgendamiento}
                                data={paginatedData}
                                isLoading={isLoading}
                                pagination={pagination}
                                onPaging={setPagination}
                                rowCount={filteredData.length}
                                enableColumnActions={false}
                                enableEditing={false}
                                enableGlobalFilter={false}
                                enableColumnFilters={true}
                            />
                        </>
                    </CustomTabPanel>
                </Grid>
            </Grid>
        </SingleTableBoxScene>
    );
};

export default ReporteAgendamientosPylPage;
